import React, { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode.react';

import { Button } from 'pctComponents/atoms';

import {
    createUserMfaSetup,
    getUserMfaStatus,
    verifyUserMfaSetup,
} from '../../services/api';
import { MFA_SETUP_INSTRUCTIONS } from './mfaSetupInstructions';
import { isMfaSetupActionDisabled } from './mfaLoginState';
import MFA_TEXTS from './mfaTexts';

import './profileMfaSettings.scss';

const normalizeCode = (value) => String(value || '').replace(/\D/g, '').slice(0, 6);

function ProfileMfaSettings({ idUser, variant = 'legacy', autoStart = false, onActivated, onCancel }) {
    const [status, setStatus] = useState(null);
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(Boolean(autoStart));
    const [feedback, setFeedback] = useState({ type: '', message: '' });
    const [autoStartPending, setAutoStartPending] = useState(Boolean(autoStart));
    const autoStartRef = useRef(false);

    const loadStatus = useCallback(async () => {
        if (!idUser) {
            setLoading(false);
            setAutoStartPending(false);
            return;
        }

        try {
            setLoading(true);
            const { data } = await getUserMfaStatus(idUser);
            setStatus(data);
            setFeedback({ type: '', message: '' });
            if (!autoStart || data.mfaActive || !data.canEnable) {
                setAutoStartPending(false);
            }
        } catch {
            setFeedback({ type: 'error', message: MFA_TEXTS.selfService.loadError });
            setAutoStartPending(false);
        } finally {
            setLoading(false);
        }
    }, [autoStart, idUser]);

    useEffect(() => {
        loadStatus();
    }, [loadStatus]);

    const startSetup = useCallback(async () => {
        if (!idUser) {
            setAutoStartPending(false);
            return;
        }

        try {
            setLoading(true);
            const { data } = await createUserMfaSetup(idUser);

            if (data.mfaActive) {
                setStatus((prev) => ({ ...prev, mfaActive: true, canEnable: false }));
                setFeedback({ type: '', message: '' });
                return;
            }

            setChallenge(data.mfa);
            setCode('');
            setFeedback({ type: '', message: '' });
        } catch {
            setFeedback({ type: 'error', message: MFA_TEXTS.selfService.startError });
        } finally {
            setLoading(false);
            setAutoStartPending(false);
        }
    }, [idUser]);

    useEffect(() => {
        if (!autoStart || autoStartRef.current || !status?.canEnable || challenge) {
            return;
        }

        autoStartRef.current = true;
        setAutoStartPending(true);
        startSetup();
    }, [autoStart, challenge, startSetup, status]);

    const verifySetup = async () => {
        if (code.length !== 6) {
            setFeedback({ type: 'error', message: MFA_TEXTS.selfService.codeRequired });
            return;
        }

        try {
            setLoading(true);
            await verifyUserMfaSetup(idUser, code);
            setChallenge(null);
            setCode('');
            setStatus((prev) => ({ ...prev, mfaActive: true, canEnable: false }));
            setFeedback({ type: '', message: '' });
            if (typeof onActivated === 'function') {
                onActivated();
            }
        } catch (error) {
            setFeedback({
                type: 'error',
                message: error?.response?.data?.message || MFA_TEXTS.selfService.invalidCode,
            });
        } finally {
            setLoading(false);
        }
    };

    const disabledByPolicy = Boolean(status?.lockedByCompanyPolicy);
    const active = Boolean(status?.mfaActive);
    const showActiveState = active && !challenge;
    const rootClassName = [
        'profileMfaSettings',
        `profileMfaSettings--${variant}`,
        showActiveState ? 'profileMfaSettings--activeState' : '',
    ].filter(Boolean).join(' ');
    const waitingForAutoStart = autoStart
        && autoStartPending
        && !challenge
        && !active
        && !feedback.message;
    const canShowStandaloneActivation = !active && (!autoStart || feedback.type === 'error');
    const title = showActiveState ? MFA_TEXTS.selfService.activeTitle : MFA_TEXTS.selfService.title;
    const description = showActiveState
        ? MFA_TEXTS.selfService.activeDescription
        : MFA_TEXTS.selfService.setupDescription;

    return (
        <div className={rootClassName}>
            <div className="profileMfaSettings__header">
                <h3>{title}</h3>
                {variant === 'legacy' && (
                    <span className={active ? 'is-active' : 'is-inactive'}>
                        {active ? MFA_TEXTS.selfService.activeLabel : MFA_TEXTS.selfService.inactiveLabel}
                    </span>
                )}
            </div>

            {!waitingForAutoStart && !challenge && <p>{description}</p>}

            {disabledByPolicy && (
                <div className="profileMfaSettings__locked">
                    {MFA_TEXTS.selfService.lockedByPolicy}
                </div>
            )}

            {!challenge && (
                <div className="profileMfaSettings__actions profileMfaSettings__actions--standalone">
                    {waitingForAutoStart && (
                        <div className="profileMfaSettings__loading">{MFA_TEXTS.selfService.loadingAction}</div>
                    )}
                    {canShowStandaloneActivation && (
                        <Button
                            className="button04 active profileMfaSettings__button"
                            disabled={isMfaSetupActionDisabled({ loading, status })}
                            callEvent={startSetup}
                            text={loading ? MFA_TEXTS.selfService.loadingAction : MFA_TEXTS.selfService.configureAction}
                        />
                    )}
                    {showActiveState && typeof onCancel === 'function' && (
                        <Button
                            className={[
                                'button04',
                                showActiveState ? 'active' : '',
                                'profileMfaSettings__button',
                                'profileMfaSettings__close',
                            ].filter(Boolean).join(' ')}
                            disabled={loading}
                            callEvent={onCancel}
                            text={MFA_TEXTS.selfService.closeAction}
                        />
                    )}
                </div>
            )}

            {challenge && (
                <div className="profileMfaSettings__setup">
                    <div className="profileMfaSettings__setupPanel">
                        <p className="profileMfaSettings__setupIntro">{description}</p>

                        <ol className="profileMfaSettings__instructions">
                            {MFA_SETUP_INSTRUCTIONS.map((instruction) => (
                                <li key={instruction}>{instruction}</li>
                            ))}
                        </ol>

                        {challenge.otpauthUrl && (
                            <div className="profileMfaSettings__qrcode">
                                <QRCode value={challenge.otpauthUrl} size={160} />
                            </div>
                        )}

                        {challenge.secret && (
                            <div className="profileMfaSettings__secret">
                                <span>{MFA_TEXTS.selfService.manualKeyLabel}</span>
                                <strong>{challenge.secret}</strong>
                            </div>
                        )}
                    </div>

                    <div className="profileMfaSettings__codeGroup">
                        <label className="profileMfaSettings__codeLabel" htmlFor="profileMfaCode">
                            {MFA_TEXTS.selfService.verificationCodeLabel}
                        </label>
                        <input
                            id="profileMfaCode"
                            type="text"
                            inputMode="numeric"
                            value={code}
                            maxLength={6}
                            placeholder={MFA_TEXTS.selfService.verificationCodePlaceholder}
                            onChange={(event) => setCode(normalizeCode(event.target.value))}
                        />
                    </div>

                    <div className="profileMfaSettings__actions">
                        <Button
                            className="button04 active profileMfaSettings__button"
                            disabled={loading || code.length !== 6}
                            callEvent={verifySetup}
                            text={loading ? MFA_TEXTS.selfService.validatingAction : MFA_TEXTS.selfService.confirmAction}
                        />
                        <Button
                            className="button04 profileMfaSettings__button"
                            disabled={loading}
                            callEvent={() => {
                                setChallenge(null);
                                setCode('');
                                if (typeof onCancel === 'function') {
                                    onCancel();
                                }
                            }}
                            text={MFA_TEXTS.selfService.cancelAction}
                        />
                    </div>
                </div>
            )}

            {feedback.message && (
                <div className={`profileMfaSettings__feedback ${feedback.type}`}>
                    {feedback.message}
                </div>
            )}
        </div>
    );
}

export default ProfileMfaSettings;
